import type {
  SeasonalActualRow, SeasonalTargetRow,
} from '../domain/sources/sources.js';
import type { ClientRankingRow, ProductRankingRow } from '../domain/entities/Rankings.js';
import type { WeeklyEvolutionPoint } from '../domain/entities/WeeklyEvolution.js';
import type { Season } from '../domain/entities/Season.js';
import type { DashboardFilters } from '../domain/entities/DashboardView.js';
import type { Client } from '../domain/entities/Client.js';
import { previousSeason } from '../domain/entities/Season.js';

/**
 * Sentinel agent id meaning "all agents" — used by the admin view to see the
 * aggregated business without segmenting by a single agent.
 */
export const ALL_AGENTS = '__all__';

/** True if a row's agent id should be included for the requested agentId.
 * The ALL_AGENTS sentinel matches every row. */
function matchesAgent(rowAgentId: string, agentId: string): boolean {
  return agentId === ALL_AGENTS || rowAgentId === agentId;
}

/** Apply line/category filters to a row. Season is filtered separately
 * by the caller because the comparison row picks a different season. */
function matchesFilter(row: { line: string; category: string }, f: DashboardFilters): boolean {
  if (f.line && row.line !== f.line) return false;
  if (f.category && row.category !== f.category) return false;
  return true;
}

/** Which clients belong to this agent? Anyone who appears in the
 * multi-season actuals with this agente_id, in any season.
 * For ALL_AGENTS this returns every client (the admin sees the whole map). */
export function clientsForAgent(actuals: SeasonalActualRow[], agentId: string): Set<string> {
  const out = new Set<string>();
  for (const r of actuals) if (matchesAgent(r.agenteId, agentId)) out.add(r.clienteId);
  return out;
}

/** Distinct lines this agent has ever sold (powers the line filter dropdown). */
export function distinctLines(actuals: SeasonalActualRow[], agentId: string): string[] {
  const s = new Set<string>();
  for (const r of actuals) if (matchesAgent(r.agenteId, agentId)) s.add(r.line);
  return Array.from(s).sort();
}

export function distinctCategories(actuals: SeasonalActualRow[], agentId: string): string[] {
  const s = new Set<string>();
  for (const r of actuals) if (matchesAgent(r.agenteId, agentId)) s.add(r.category);
  return Array.from(s).sort();
}

interface AggBag { revenue: number; units: number; clients: Set<string> }
function emptyBag(): AggBag { return { revenue: 0, units: 0, clients: new Set() }; }

/**
 * Aggregate seasonal actuals into agent-scoped totals respecting filters.
 * `seasonOverride` lets the caller compute the same totals for a different
 * season (used for last-season comparison).
 */
function aggregateAgent(
  actuals: SeasonalActualRow[],
  agentId: string,
  season: Season,
  filters: DashboardFilters,
): AggBag {
  const bag = emptyBag();
  for (const r of actuals) {
    if (!matchesAgent(r.agenteId, agentId)) continue;
    if (r.season !== season) continue;
    if (!matchesFilter(r, filters)) continue;
    bag.revenue += r.revenueEUR;
    bag.units += r.units;
    bag.clients.add(r.clienteId);
  }
  return bag;
}

/**
 * Build top-N client ranking sorted by current-season revenue.
 */
export function buildClientRanking(
  actuals: SeasonalActualRow[],
  targets: SeasonalTargetRow[],
  clients: Client[],
  agentId: string,
  season: Season,
  filters: DashboardFilters,
  topN = 20,
): ClientRankingRow[] {
  const prev = previousSeason(season);
  const cur = new Map<string, AggBag>();
  const last = new Map<string, AggBag>();
  const tgt = new Map<string, { revenue: number; units: number }>();

  for (const r of actuals) {
    if (!matchesAgent(r.agenteId, agentId)) continue;
    if (!matchesFilter(r, filters)) continue;
    if (r.season === season) {
      const b = cur.get(r.clienteId) ?? emptyBag();
      b.revenue += r.revenueEUR; b.units += r.units; b.clients.add(r.clienteId);
      cur.set(r.clienteId, b);
    } else if (prev && r.season === prev) {
      const b = last.get(r.clienteId) ?? emptyBag();
      b.revenue += r.revenueEUR; b.units += r.units;
      last.set(r.clienteId, b);
    }
  }
  for (const t of targets) {
    if (!matchesAgent(t.agenteId, agentId)) continue;
    if (t.season !== season) continue;
    if (!matchesFilter(t, filters)) continue;
    const acc = tgt.get(t.clienteId) ?? { revenue: 0, units: 0 };
    acc.revenue += t.revenueTargetEUR;
    acc.units += t.unitsTarget;
    tgt.set(t.clienteId, acc);
  }

  const clientName = new Map(clients.map((c) => [c.id, c.commercialName]));

  const rows: ClientRankingRow[] = Array.from(cur.entries()).map(([id, b]) => {
    const ly = last.get(id)?.revenue ?? 0;
    const target = tgt.get(id)?.revenue ?? 0;
    return {
      clientId: id,
      commercialName: clientName.get(id) ?? id,
      revenueEUR: round2(b.revenue),
      units: Math.round(b.units),
      revenuePreviousSeasonEUR: round2(ly),
      revenueTargetEUR: round2(target),
      yoyPct: ly > 0 ? b.revenue / ly - 1 : (b.revenue > 0 ? Number.POSITIVE_INFINITY : 0),
      targetPct: target > 0 ? b.revenue / target : 0,
    };
  });
  rows.sort((a, b) => b.revenueEUR - a.revenueEUR);
  return rows.slice(0, topN);
}

/**
 * Build top product ranking aggregated by category in the active season.
 */
export function buildProductRanking(
  actuals: SeasonalActualRow[],
  agentId: string,
  season: Season,
  filters: DashboardFilters,
): ProductRankingRow[] {
  const prev = previousSeason(season);
  const cur = new Map<string, { revenue: number; units: number }>();
  const last = new Map<string, number>();
  let totalRevenue = 0;

  for (const r of actuals) {
    if (!matchesAgent(r.agenteId, agentId)) continue;
    if (!matchesFilter(r, filters)) continue;
    if (r.season === season) {
      const acc = cur.get(r.category) ?? { revenue: 0, units: 0 };
      acc.revenue += r.revenueEUR;
      acc.units += r.units;
      cur.set(r.category, acc);
      totalRevenue += r.revenueEUR;
    } else if (prev && r.season === prev) {
      last.set(r.category, (last.get(r.category) ?? 0) + r.revenueEUR);
    }
  }
  const rows: ProductRankingRow[] = Array.from(cur.entries()).map(([category, v]) => {
    const ly = last.get(category) ?? 0;
    return {
      category,
      revenueEUR: round2(v.revenue),
      units: Math.round(v.units),
      share: totalRevenue > 0 ? v.revenue / totalRevenue : 0,
      revenuePreviousSeasonEUR: round2(ly),
      yoyPct: ly > 0 ? v.revenue / ly - 1 : (v.revenue > 0 ? Number.POSITIVE_INFINITY : 0),
    };
  });
  rows.sort((a, b) => b.revenueEUR - a.revenueEUR);
  return rows;
}

/**
 * Build the per-week evolution for the active season vs (a) last season
 * and (b) targets. Three KPIs per point so the UI can toggle without
 * re-aggregating.
 */
export function buildWeeklyEvolution(
  actuals: SeasonalActualRow[],
  targets: SeasonalTargetRow[],
  agentId: string,
  season: Season,
  filters: DashboardFilters,
): WeeklyEvolutionPoint[] {
  const prev = previousSeason(season);
  // weekNumber → buckets
  type Cell = { revenue: number; units: number; clients: Set<string> };
  const cellOf = (): Cell => ({ revenue: 0, units: 0, clients: new Set() });
  const cur = new Map<number, Cell>();
  const ly = new Map<number, Cell>();
  const tgt = new Map<number, { revenue: number; units: number; clients: Set<string> }>();

  for (const r of actuals) {
    if (!matchesAgent(r.agenteId, agentId)) continue;
    if (!matchesFilter(r, filters)) continue;
    if (r.season === season) {
      const c = cur.get(r.weekNumber) ?? cellOf();
      c.revenue += r.revenueEUR; c.units += r.units; c.clients.add(r.clienteId);
      cur.set(r.weekNumber, c);
    } else if (prev && r.season === prev) {
      const c = ly.get(r.weekNumber) ?? cellOf();
      c.revenue += r.revenueEUR; c.units += r.units; c.clients.add(r.clienteId);
      ly.set(r.weekNumber, c);
    }
  }
  for (const t of targets) {
    if (!matchesAgent(t.agenteId, agentId)) continue;
    if (t.season !== season) continue;
    if (!matchesFilter(t, filters)) continue;
    const c = tgt.get(t.weekNumber) ?? { revenue: 0, units: 0, clients: new Set<string>() };
    c.revenue += t.revenueTargetEUR;
    c.units += t.unitsTarget;
    c.clients.add(t.clienteId);
    tgt.set(t.weekNumber, c);
  }

  const allWeeks = new Set<number>([...cur.keys(), ...ly.keys(), ...tgt.keys()]);
  const sortedWeeks = Array.from(allWeeks).sort((a, b) => a - b);

  return sortedWeeks.map((w): WeeklyEvolutionPoint => {
    const a = cur.get(w) ?? cellOf();
    const b = ly.get(w) ?? cellOf();
    const t = tgt.get(w) ?? { revenue: 0, units: 0, clients: new Set<string>() };
    return {
      weekNumber: w,
      label: `Sem ${w}`,
      revenueActual: round2(a.revenue),
      revenueLastSeason: round2(b.revenue),
      revenueTarget: round2(t.revenue),
      unitsActual: Math.round(a.units),
      unitsLastSeason: Math.round(b.units),
      unitsTarget: Math.round(t.units),
      clientsActual: a.clients.size,
      clientsLastSeason: b.clients.size,
      clientsTarget: t.clients.size,
    };
  });
}

/**
 * Active-season KPIs for the agent under current filters.
 * Returns the three current numbers plus their previous-season and target counterparts.
 */
export function computeAgentSeasonalKPIs(
  actuals: SeasonalActualRow[],
  targets: SeasonalTargetRow[],
  agentId: string,
  season: Season,
  filters: DashboardFilters,
) {
  const prev = previousSeason(season);
  const cur = aggregateAgent(actuals, agentId, season, filters);
  const last = prev ? aggregateAgent(actuals, agentId, prev, filters) : emptyBag();

  let tgtRevenue = 0, tgtUnits = 0;
  const tgtClients = new Set<string>();
  for (const t of targets) {
    if (!matchesAgent(t.agenteId, agentId)) continue;
    if (t.season !== season) continue;
    if (!matchesFilter(t, filters)) continue;
    tgtRevenue += t.revenueTargetEUR;
    tgtUnits += t.unitsTarget;
    tgtClients.add(t.clienteId);
  }

  return {
    revenue:     { current: cur.revenue,   previousYear: last.revenue,   target: tgtRevenue },
    units:       { current: cur.units,     previousYear: last.units,     target: tgtUnits },
    clientCount: { current: cur.clients.size, previousYear: last.clients.size, target: tgtClients.size },
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
